import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Badge } from '@/components/ui/badge';
import { Trophy, Target, Zap, Star, Crown, Medal, Flame, X, Sparkles, TrendingUp, TrendingDown, Minus, History, Award } from 'lucide-react';
import SkillsRadarChart from './SkillsRadarChart';
import PlayerLevelBadge from './PlayerLevelBadge';
import { useCountUp } from '@/hooks/useCountUp';
import { supabase } from '@/integrations/supabase/client';

interface SkillRatings {
  pace?: number;
  shooting?: number;
  passing?: number;
  dribbling?: number;
  defending?: number;
  physical?: number;
}

interface Player {
  id: string;
  name: string;
  slug: string;
  avatar?: string | null;
  position?: string | null;
  jersey_number?: number | null;
  overall_rating?: number | null;
  skill_ratings?: SkillRatings | null;
  stats?: {
    gameId?: string;
  } | null;
}

interface LeaderboardEntry {
  points: number;
  wins: number;
  draws: number;
  losses: number;
  goals_for: number;
  goals_against: number;
  matches_played: number;
}

interface MatchResult {
  id: string;
  opponent: string;
  playerScore: number;
  opponentScore: number;
  result: 'W' | 'D' | 'L';
  date: string;
}

interface PersonalBests {
  winStreak: number;
  mostGoals: number;
  biggestWin: number;
}

interface PlayerCardModalProps {
  player: Player;
  leaderboardEntry?: LeaderboardEntry | null;
  rank: number;
  isOpen: boolean;
  onClose: () => void;
}

const PlayerCardModal: React.FC<PlayerCardModalProps> = ({
  player,
  leaderboardEntry,
  rank,
  isOpen,
  onClose
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [matchHistory, setMatchHistory] = useState<MatchResult[]>([]);
  const [personalBests, setPersonalBests] = useState<PersonalBests>({ winStreak: 0, mostGoals: 0, biggestWin: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile for performance optimization
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Animated counters
  const animatedPoints = useCountUp({ end: leaderboardEntry?.points || 0, duration: 1500, enabled: isOpen });
  const animatedWins = useCountUp({ end: leaderboardEntry?.wins || 0, duration: 1500, delay: 100, enabled: isOpen });
  const animatedGoals = useCountUp({ end: leaderboardEntry?.goals_for || 0, duration: 1500, delay: 200, enabled: isOpen });
  const animatedWinRate = useCountUp({ 
    end: leaderboardEntry ? Math.round((leaderboardEntry.wins / Math.max(leaderboardEntry.matches_played, 1)) * 100) : 0, 
    duration: 1500, 
    delay: 300, 
    enabled: isOpen 
  });

  // Fetch match history and calculate personal bests
  useEffect(() => {
    if (isOpen && player.id) {
      fetchMatchData();
    }
  }, [isOpen, player.id]);

  const fetchMatchData = async () => {
    setIsLoading(true);
    try {
      const { data: matches, error } = await supabase
        .from('matches')
        .select(`
          id,
          score_a,
          score_b,
          team_a,
          team_b,
          match_date,
          team_a_player:players!matches_team_a_fkey(name),
          team_b_player:players!matches_team_b_fkey(name)
        `)
        .or(`team_a.eq.${player.id},team_b.eq.${player.id}`)
        .eq('status', 'completed')
        .order('match_date', { ascending: false })
        .order('id', { ascending: true })
        .limit(10);

      if (error) throw error;

      // Process match history
      const history: MatchResult[] = (matches || []).slice(0, 5).map((match: any) => {
        const isTeamA = match.team_a === player.id;
        const playerScore = isTeamA ? (match.score_a || 0) : (match.score_b || 0);
        const opponentScore = isTeamA ? (match.score_b || 0) : (match.score_a || 0);
        const opponentName = isTeamA 
          ? (match.team_b_player?.name || 'Unknown')
          : (match.team_a_player?.name || 'Unknown');
        
        let result: 'W' | 'D' | 'L' = 'D';
        if (playerScore > opponentScore) result = 'W';
        else if (playerScore < opponentScore) result = 'L';

        return {
          id: match.id,
          opponent: opponentName,
          playerScore,
          opponentScore,
          result,
          date: match.match_date
        };
      });

      setMatchHistory(history);

      // Calculate personal bests
      let currentStreak = 0;
      let maxStreak = 0;
      let mostGoals = 0;
      let biggestWin = 0;

      (matches || []).forEach((match: any) => {
        const isTeamA = match.team_a === player.id;
        const playerScore = isTeamA ? (match.score_a || 0) : (match.score_b || 0);
        const opponentScore = isTeamA ? (match.score_b || 0) : (match.score_a || 0);

        // Most goals
        if (playerScore > mostGoals) mostGoals = playerScore;

        // Biggest win
        const margin = playerScore - opponentScore;
        if (margin > biggestWin) biggestWin = margin;

        // Win streak
        if (playerScore > opponentScore) {
          currentStreak++;
          if (currentStreak > maxStreak) maxStreak = currentStreak;
        } else {
          currentStreak = 0;
        }
      });

      setPersonalBests({ winStreak: maxStreak, mostGoals, biggestWin: Math.max(0, biggestWin) });
    } catch (error) {
      console.error('Error fetching match data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTierConfig = (rating: number) => {
    if (rating >= 85) return { 
      tier: 'Epic', 
      color: 'from-purple-500 via-pink-500 to-cyan-400', 
      glow: 'premium-glow-epic',
      bgClass: 'premium-card-epic',
      textColor: 'text-purple-300',
      borderColor: 'border-purple-400/50',
      particleClass: 'particles-epic'
    };
    if (rating >= 75) return { 
      tier: 'Gold', 
      color: 'from-yellow-400 via-amber-500 to-orange-500', 
      glow: 'premium-glow-gold',
      bgClass: 'premium-card-gold',
      textColor: 'text-yellow-300',
      borderColor: 'border-yellow-400/50',
      particleClass: 'particles-gold'
    };
    if (rating >= 65) return { 
      tier: 'Silver', 
      color: 'from-gray-300 via-slate-400 to-gray-500', 
      glow: 'premium-glow-silver',
      bgClass: 'premium-card-silver',
      textColor: 'text-slate-300',
      borderColor: 'border-slate-400/50',
      particleClass: 'particles-silver'
    };
    return { 
      tier: 'Bronze', 
      color: 'from-orange-600 via-amber-700 to-yellow-800', 
      glow: 'premium-glow-bronze',
      bgClass: 'premium-card-bronze',
      textColor: 'text-orange-300',
      borderColor: 'border-orange-400/50',
      particleClass: 'particles-bronze'
    };
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)]" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-purple-400 drop-shadow-[0_0_8px_rgba(192,132,252,0.8)]" />;
    return <Medal className="w-5 h-5 text-red-400 drop-shadow-[0_0_8px_rgba(248,113,113,0.8)]" />;
  };

  const getFormTrend = () => {
    if (matchHistory.length < 3) return { icon: Minus, color: 'text-muted-foreground', label: 'New' };
    const recentWins = matchHistory.slice(0, 3).filter(m => m.result === 'W').length;
    if (recentWins >= 2) return { icon: TrendingUp, color: 'text-emerald-400', label: 'Hot' };
    if (recentWins === 0) return { icon: TrendingDown, color: 'text-red-400', label: 'Cold' };
    return { icon: Minus, color: 'text-yellow-400', label: 'Stable' };
  };

  const overall = player.overall_rating || 70;
  const tierConfig = getTierConfig(overall);
  const formTrend = getFormTrend();
  const FormIcon = formTrend.icon;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || window.innerWidth < 768) return; // Disable 3D on mobile
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    
    setRotateY((mouseX / rect.width) * 15);
    setRotateX(-(mouseY / rect.height) * 15);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setIsHovering(false);
  };

  const achievements = [
    { id: 'top3', name: 'Top 3', icon: Trophy, unlocked: rank <= 3, color: 'text-yellow-400', glowColor: 'shadow-[0_0_15px_rgba(250,204,21,0.5)]', progress: rank <= 3 ? 100 : Math.max(0, (10 - rank) * 10), target: 'Top 3' },
    { id: 'winner', name: 'Champion', icon: Crown, unlocked: rank === 1, color: 'text-purple-400', glowColor: 'shadow-[0_0_15px_rgba(192,132,252,0.5)]', progress: rank === 1 ? 100 : Math.max(0, (5 - rank) * 20), target: '#1' },
    { id: 'streak', name: 'Streak', icon: Flame, unlocked: personalBests.winStreak >= 5, color: 'text-orange-400', glowColor: 'shadow-[0_0_15px_rgba(251,146,60,0.5)]', progress: Math.min(100, (personalBests.winStreak / 5) * 100), target: `${Math.max(0, 5 - personalBests.winStreak)}W` },
    { id: 'scorer', name: 'Scorer', icon: Target, unlocked: (leaderboardEntry?.goals_for || 0) >= 10, color: 'text-cyan-400', glowColor: 'shadow-[0_0_15px_rgba(34,211,238,0.5)]', progress: Math.min(100, ((leaderboardEntry?.goals_for || 0) / 10) * 100), target: `${Math.max(0, 10 - (leaderboardEntry?.goals_for || 0))}G` },
    { id: 'veteran', name: 'Veteran', icon: Star, unlocked: (leaderboardEntry?.matches_played || 0) >= 20, color: 'text-emerald-400', glowColor: 'shadow-[0_0_15px_rgba(52,211,153,0.5)]', progress: Math.min(100, ((leaderboardEntry?.matches_played || 0) / 20) * 100), target: `${Math.max(0, 20 - (leaderboardEntry?.matches_played || 0))}M` },
    { id: 'elite', name: 'Elite', icon: Zap, unlocked: overall >= 80, color: 'text-pink-400', glowColor: 'shadow-[0_0_15px_rgba(244,114,182,0.5)]', progress: Math.min(100, ((overall - 60) / 20) * 100), target: `${Math.max(0, 80 - overall)}+` },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[100vw] h-[100vh] max-w-none max-h-none md:max-w-4xl md:h-auto md:max-h-[95vh] md:w-[95vw] bg-background/95 backdrop-blur-xl border-none md:border md:border-border/50 shadow-none md:shadow-2xl p-0 md:rounded-2xl rounded-none overflow-hidden" aria-describedby={undefined}>
        {/* Accessibility: Hidden title for screen readers */}
        <VisuallyHidden>
          <DialogTitle>{player.name} - Player Details</DialogTitle>
        </VisuallyHidden>
        {/* Mobile-optimized scrollable container */}
        <div className="h-full overflow-y-auto overscroll-contain">
          <div className="relative min-h-full">
            {/* Floating Particles Background - Reduced on mobile for performance */}
            <div className={`absolute inset-0 overflow-hidden pointer-events-none ${tierConfig.particleClass}`}>
              {[...Array(isMobile ? 5 : 15)].map((_, i) => (
                <div
                  key={i}
                  className="floating-particle absolute rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`,
                    animationDuration: `${isMobile ? 8 + Math.random() * 4 : 3 + Math.random() * 4}s`
                  }}
                />
              ))}
            </div>

            {/* Fixed Close button for mobile - positioned below notch */}
            <button 
              onClick={onClose}
              className="fixed md:absolute top-14 right-4 md:top-4 md:-right-3 z-50 w-10 h-10 rounded-full bg-background/95 backdrop-blur-xl border border-border/50 flex items-center justify-center active:scale-95 transition-all duration-200 shadow-lg"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Mobile: Single column layout */}
            <div className="flex flex-col gap-4 p-4 pt-20 md:pt-4 pb-8">
              
              {/* Player Card - Smaller & Compact for mobile */}
              <div 
                className="flex items-center justify-center"
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  ref={cardRef}
                  className={`relative w-[70vw] max-w-[240px] md:w-[85vw] md:max-w-[280px] aspect-[2.8/4] md:aspect-[3/4] rounded-2xl transition-all duration-300 ease-out ${tierConfig.glow}`}
                  style={{
                    transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${isHovering ? 1.02 : 1})`,
                    transformStyle: 'preserve-3d'
                  }}
                >
                  {/* Premium Card Background */}
                  <div className={`absolute inset-0 rounded-2xl ${tierConfig.bgClass}`} />
                  
                  {/* Holographic shimmer overlay */}
                  <div className="premium-holographic-shimmer absolute inset-0 rounded-2xl pointer-events-none" />
                  
                  {/* Glass reflection */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Animated border glow */}
                  <div className={`absolute inset-0 rounded-2xl border-2 ${tierConfig.borderColor} premium-border-glow`} />

                  {/* Card Content */}
                  <div className="relative z-10 h-full flex flex-col p-3 md:p-4 text-white">
                    {/* Header with OVR and Rank */}
                    <div className="flex justify-between items-start">
                      <div className="flex flex-col items-center">
                        <span className="text-3xl md:text-4xl font-black drop-shadow-[0_0_15px_rgba(255,255,255,0.5)] premium-text-glow">{overall}</span>
                        <Badge className={`mt-1 text-[8px] md:text-[9px] premium-tier-badge ${
                          tierConfig.tier === 'Epic' ? 'bg-purple-500/40 border-purple-400/50' : 
                          tierConfig.tier === 'Gold' ? 'bg-yellow-500/40 border-yellow-400/50' : 
                          tierConfig.tier === 'Silver' ? 'bg-slate-400/40 border-slate-300/50' : 
                          'bg-orange-500/40 border-orange-400/50'
                        } border backdrop-blur-sm px-1.5 md:px-2 py-0.5`}>
                          <Sparkles className="w-2 h-2 md:w-2.5 md:h-2.5 mr-0.5" />
                          {tierConfig.tier}
                        </Badge>
                      </div>
                      <div className="text-right flex flex-col items-center">
                        <div className="premium-rank-icon">{getRankIcon(rank)}</div>
                        <span className="block text-sm md:text-base font-black mt-0.5 drop-shadow-lg">#{rank}</span>
                      </div>
                    </div>

                    {/* Avatar with premium stadium background */}
                    <div className="flex-1 flex items-center justify-center py-1 md:py-2">
                      <div className="relative">
                        {/* Stadium/Hexagon Background Pattern behind avatar */}
                        <div className="absolute -inset-4 md:-inset-6 rounded-2xl overflow-hidden">
                          <div className="absolute inset-0 stadium-pattern opacity-40" />
                          <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/80" />
                          <div className={`absolute inset-0 ${
                            rank === 1 ? 'bg-gradient-to-t from-yellow-500/20 via-transparent to-amber-400/10' :
                            rank === 2 ? 'bg-gradient-to-t from-purple-500/20 via-transparent to-pink-400/10' :
                            'bg-gradient-to-t from-red-500/20 via-transparent to-orange-400/10'
                          }`} />
                        </div>
                        
                        <div className={`absolute inset-0 rounded-full premium-avatar-glow ${
                          rank === 1 ? 'bg-gradient-to-r from-yellow-400 to-amber-500' :
                          rank === 2 ? 'bg-gradient-to-r from-purple-400 to-pink-500' :
                          'bg-gradient-to-r from-red-400 to-orange-500'
                        } blur-lg opacity-50 scale-110`} />
                        <div className={`relative w-16 h-16 md:w-20 md:h-20 rounded-full border-2 md:border-3 overflow-hidden ${
                          rank === 1 ? 'border-yellow-400/80' :
                          rank === 2 ? 'border-purple-400/80' :
                          'border-red-400/80'
                        } shadow-xl`}>
                          {player.avatar ? (
                            <img src={player.avatar} alt={player.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-white/30 to-white/10 flex items-center justify-center backdrop-blur-sm">
                              <span className="text-xl md:text-2xl font-black">{player.name.charAt(0)}</span>
                            </div>
                          )}
                        </div>
                        {player.position && (
                          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 md:px-2 py-0.5 rounded-full bg-black/70 backdrop-blur-md text-[8px] md:text-[9px] font-bold border border-white/20">
                            {player.position}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Name & Game ID */}
                    <div className="text-center">
                      <h3 className="text-sm md:text-base font-black truncate drop-shadow-lg premium-text-glow">{player.name}</h3>
                      {player.stats?.gameId && (
                        <p className="text-[9px] md:text-[10px] opacity-80 font-medium">{player.stats.gameId}</p>
                      )}
                    </div>

                    {/* Premium Animated Stats Row */}
                    <div className="grid grid-cols-3 gap-1 md:gap-1.5 mt-1.5 md:mt-2 text-center">
                      {[
                        { value: animatedPoints, label: 'PTS' },
                        { value: animatedWins, label: 'WIN' },
                        { value: animatedGoals, label: 'GLS' }
                      ].map((stat, idx) => (
                        <div key={idx} className="premium-stat-box bg-black/40 backdrop-blur-sm rounded-lg py-1 md:py-1.5 border border-white/10">
                          <span className="block text-sm md:text-base font-black premium-stat-number">{stat.value}</span>
                          <span className="text-[7px] md:text-[8px] opacity-70 font-semibold">{stat.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Best Records - Horizontal scroll on mobile */}
              <div className="bg-card/80 backdrop-blur-xl rounded-xl p-3 border border-border/40">
                <h4 className="text-xs font-bold text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5 text-amber-500" />
                  Personal Bests
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/5 rounded-lg p-2.5 text-center border border-orange-500/30">
                    <Flame className="w-4 h-4 mx-auto text-orange-400 mb-0.5" />
                    <div className="text-lg font-black text-orange-400">{personalBests.winStreak}</div>
                    <div className="text-[8px] text-muted-foreground">Streak</div>
                  </div>
                  <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/5 rounded-lg p-2.5 text-center border border-cyan-500/30">
                    <Target className="w-4 h-4 mx-auto text-cyan-400 mb-0.5" />
                    <div className="text-lg font-black text-cyan-400">{personalBests.mostGoals}</div>
                    <div className="text-[8px] text-muted-foreground">Goals</div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 rounded-lg p-2.5 text-center border border-emerald-500/30">
                    <TrendingUp className="w-4 h-4 mx-auto text-emerald-400 mb-0.5" />
                    <div className="text-lg font-black text-emerald-400">+{personalBests.biggestWin}</div>
                    <div className="text-[8px] text-muted-foreground">Margin</div>
                  </div>
                </div>
              </div>

              {/* Player Level Badge */}
              <PlayerLevelBadge 
                points={leaderboardEntry?.points || 0} 
                matchesPlayed={leaderboardEntry?.matches_played || 0} 
              />

              {/* Form Trend & Match History */}
              <div className="bg-card/80 backdrop-blur-xl rounded-xl p-3 border border-border/40">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5 text-primary" />
                    Recent Form
                  </h4>
                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${formTrend.color} bg-background/50 border border-current/20`}>
                    <FormIcon className="w-2.5 h-2.5" />
                    {formTrend.label}
                  </div>
                </div>

                {/* Form dots - larger touch targets */}
                <div className="flex items-center gap-2 mb-2">
                  {matchHistory.slice(0, 5).map((match, idx) => (
                    <div 
                      key={match.id}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all active:scale-95 ${
                        match.result === 'W' ? 'bg-emerald-500/25 text-emerald-400 border border-emerald-500/50' :
                        match.result === 'L' ? 'bg-red-500/25 text-red-400 border border-red-500/50' :
                        'bg-yellow-500/25 text-yellow-400 border border-yellow-500/50'
                      }`}
                      style={{ animationDelay: `${idx * 100}ms` }}
                    >
                      {match.result}
                    </div>
                  ))}
                  {matchHistory.length === 0 && (
                    <span className="text-xs text-muted-foreground">No matches yet</span>
                  )}
                </div>

                {/* Match history list - scrollable */}
                <div className="space-y-1.5 max-h-28 overflow-y-auto">
                  {matchHistory.slice(0, 4).map((match, idx) => (
                    <div 
                      key={match.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-background/50 border border-border/20 text-xs"
                      style={{ animationDelay: `${idx * 100}ms` }}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold ${
                          match.result === 'W' ? 'bg-emerald-500/30 text-emerald-400' :
                          match.result === 'L' ? 'bg-red-500/30 text-red-400' :
                          'bg-yellow-500/30 text-yellow-400'
                        }`}>
                          {match.result}
                        </div>
                        <span className="text-muted-foreground truncate max-w-[80px]">vs {match.opponent}</span>
                      </div>
                      <div className="font-bold tabular-nums">
                        {match.playerScore}-{match.opponentScore}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Skills Radar Chart */}
              <div className="bg-card/80 backdrop-blur-xl rounded-xl p-3 border border-border/40">
                <h4 className="text-xs font-bold text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-primary" />
                  Skills
                </h4>
                <div className="flex justify-center">
                  <SkillsRadarChart 
                    skills={player.skill_ratings || {}}
                    size={180}
                    tier={tierConfig.tier}
                  />
                </div>
              </div>

              {/* Achievements Grid - 2x3 on mobile */}
              <div className="bg-card/80 backdrop-blur-xl rounded-xl p-3 border border-border/40">
                <h4 className="text-xs font-bold text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Trophy className="w-3.5 h-3.5 text-yellow-500" />
                  Achievements
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  {achievements.map((achievement) => {
                    const Icon = achievement.icon;
                    return (
                      <div 
                        key={achievement.id}
                        className={`relative flex flex-col items-center p-2.5 rounded-xl transition-all duration-300 ${
                          achievement.unlocked 
                            ? `bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/40 ${achievement.glowColor}` 
                            : 'bg-muted/20 border border-border/20'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${achievement.unlocked ? achievement.color : 'text-muted-foreground opacity-40'}`} />
                        <span className="text-[8px] mt-1 text-center text-muted-foreground font-medium">{achievement.name}</span>
                        
                        {/* Progress bar for locked achievements */}
                        {!achievement.unlocked && (
                          <div className="w-full mt-1.5">
                            <div className="h-1 bg-muted/30 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-primary/60 to-primary rounded-full"
                                style={{ width: `${achievement.progress}%` }}
                              />
                            </div>
                            <span className="text-[7px] text-muted-foreground/60 mt-0.5 block text-center">{achievement.target}</span>
                          </div>
                        )}
                        
                        {achievement.unlocked && (
                          <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full border border-background" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Performance Stats - Side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl p-3 text-center border border-primary/30 relative overflow-hidden">
                  <div className="relative">
                    {/* Circular progress ring */}
                    <div className="relative w-14 h-14 mx-auto mb-1">
                      <svg className="w-full h-full -rotate-90">
                        <circle cx="28" cy="28" r="24" fill="none" stroke="currentColor" strokeWidth="3" className="text-muted/20" />
                        <circle 
                          cx="28" cy="28" r="24" fill="none" stroke="url(#winRateGradient)" strokeWidth="3" 
                          strokeDasharray={`${(animatedWinRate / 100) * 151} 151`}
                          strokeLinecap="round"
                        />
                        <defs>
                          <linearGradient id="winRateGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="hsl(var(--primary))" />
                            <stop offset="100%" stopColor="hsl(var(--primary) / 0.6)" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-black text-primary">{animatedWinRate}%</span>
                      </div>
                    </div>
                    <div className="text-[10px] text-muted-foreground font-semibold">Win Rate</div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 rounded-xl p-3 text-center border border-emerald-500/30 relative overflow-hidden">
                  <div className="relative">
                    <div className="text-2xl font-black text-emerald-400 mb-1">
                      {(leaderboardEntry?.goals_for || 0) - (leaderboardEntry?.goals_against || 0) > 0 ? '+' : ''}
                      {(leaderboardEntry?.goals_for || 0) - (leaderboardEntry?.goals_against || 0)}
                    </div>
                    <div className="text-[10px] text-muted-foreground font-semibold">Goal Diff</div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlayerCardModal;
